<template>
  <div class="requests-page">
    <!-- Header -->
    <div class="page-header">
      <button class="back-btn" @click="goBack">
        <i class="bi bi-arrow-left"></i>
      </button>
      <h1>{{ $t("chat.message_requests") || "Message Requests" }}</h1>
      <div class="header-spacer"></div>
    </div>

    <!-- Tabs -->
    <div class="tabs-container">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'received' }"
        @click="activeTab = 'received'"
      >
        <i class="bi bi-inbox"></i>
        {{ $t("chat.received") || "Received" }}
        <span v-if="receivedCount > 0" class="badge">{{ receivedCount }}</span>
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'sent' }"
        @click="activeTab = 'sent'"
      >
        <i class="bi bi-send"></i>
        {{ $t("chat.sent") || "Sent" }}
      </button>
    </div>

    <!-- Content -->
    <div class="requests-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <span>{{ $t("common.loading") || "Loading..." }}</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <i class="bi bi-exclamation-triangle"></i>
        <span>{{ error }}</span>
        <button class="retry-btn" @click="loadRequests">
          <i class="bi bi-arrow-repeat"></i>
          {{ $t("common.retry") || "Retry" }}
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="currentRequests.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="bi bi-envelope-open"></i>
        </div>
        <h3>
          {{
            activeTab === "received"
              ? $t("chat.no_received_requests") || "No received requests"
              : $t("chat.no_sent_requests") || "No sent requests"
          }}
        </h3>
        <p>
          {{
            activeTab === "received"
              ? $t("chat.no_received_requests_desc") ||
                "When someone sends you a message request, it will appear here"
              : $t("chat.no_sent_requests_desc") ||
                "Your sent friend requests will appear here"
          }}
        </p>
      </div>

      <!-- Requests List -->
      <div v-else class="requests-list">
        <div
          v-for="request in currentRequests"
          :key="request.id"
          class="request-card"
        >
          <!-- User Avatar -->
          <div class="user-avatar">
            <img
              v-if="getUser(request)?.image"
              :src="getUser(request).image"
              :alt="getUser(request).name"
            />
            <div v-else class="avatar-placeholder">
              {{ getInitials(getUser(request)?.name) }}
            </div>
          </div>

          <!-- User Info -->
          <div class="user-info">
            <h4>{{ getUser(request)?.name || "Unknown" }}</h4>
            <span class="request-time">{{
              formatTime(request.created_at)
            }}</span>

            <!-- Request Message -->
            <div v-if="request.message" class="request-message">
              <p>{{ request.message }}</p>
            </div>

            <!-- Status Badge for Sent Requests -->
            <div
              v-if="activeTab === 'sent'"
              class="status-badge"
              :class="request.status"
            >
              <i v-if="request.status === 'pending'" class="bi bi-clock"></i>
              <i
                v-else-if="request.status === 'accepted'"
                class="bi bi-check-circle"
              ></i>
              <i
                v-else-if="request.status === 'rejected'"
                class="bi bi-x-circle"
              ></i>
              {{ getStatusLabel(request.status) }}
            </div>
          </div>

          <!-- Actions -->
          <div class="request-actions">
            <!-- Received Request Actions -->
            <template
              v-if="activeTab === 'received' && request.status === 'pending'"
            >
              <button
                class="action-btn accept-btn"
                @click="acceptRequest(request)"
                :disabled="processing === request.id"
              >
                <i class="bi bi-check-lg"></i>
                <span>{{ $t("chat.accept") || "Accept" }}</span>
              </button>
              <button
                class="action-btn reject-btn"
                @click="rejectRequest(request)"
                :disabled="processing === request.id"
              >
                <i class="bi bi-x-lg"></i>
                <span>{{ $t("chat.reject") || "Reject" }}</span>
              </button>
            </template>

            <!-- Sent Request Actions -->
            <template
              v-if="activeTab === 'sent' && request.status === 'pending'"
            >
              <button
                class="action-btn delete-btn"
                @click="deleteRequest(request)"
                :disabled="processing === request.id"
              >
                <i class="bi bi-trash3"></i>
                <span>{{ $t("chat.delete_request") || "Delete" }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ChatService } from "@/services/chat";

export default {
  name: "MessageRequests",
  data() {
    return {
      activeTab: "received",
      loading: true,
      error: null,
      receivedRequests: [],
      sentRequests: [],
      processing: null,
    };
  },
  computed: {
    currentRequests() {
      return this.activeTab === "received"
        ? this.receivedRequests
        : this.sentRequests;
    },
    receivedCount() {
      return this.receivedRequests.filter((r) => r.status === "pending").length;
    },
  },
  watch: {
    activeTab() {
      this.loadRequests();
    },
  },
  mounted() {
    // Check for tab query parameter (e.g., ?tab=sent)
    const tabParam = this.$route.query.tab;
    if (tabParam === "sent" || tabParam === "received") {
      this.activeTab = tabParam;
    }
    this.loadRequests();
  },
  methods: {
    goBack() {
      this.$router.back();
    },

    async loadRequests() {
      this.loading = true;
      this.error = null;

      try {
        if (this.activeTab === "received") {
          // Use 'pending' status for received requests
          this.receivedRequests = await ChatService.getReceivedRequests(
            "pending"
          );
        } else {
          // Use 'pending' status for sent requests
          this.sentRequests = await ChatService.getSentRequests("pending");
        }
      } catch (e) {
        this.error =
          e?.message ||
          this.$t("chat.failed_to_load_requests") ||
          "Failed to load requests";
      } finally {
        this.loading = false;
      }
    },

    getUser(request) {
      return this.activeTab === "received"
        ? request.from_user
        : request.to_user;
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },

    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return this.$t("chat.just_now") || "Just now";
      if (diffMins < 60)
        return `${diffMins} ${this.$t("chat.minutes_ago") || "min ago"}`;
      if (diffHours < 24)
        return `${diffHours} ${this.$t("chat.hours_ago") || "hours ago"}`;
      if (diffDays < 7)
        return `${diffDays} ${this.$t("chat.days_ago") || "days ago"}`;

      return date.toLocaleDateString();
    },

    getStatusLabel(status) {
      const labels = {
        pending: this.$t("chat.pending") || "Pending",
        accepted: this.$t("chat.accepted") || "Accepted",
        rejected: this.$t("chat.rejected") || "Rejected",
      };
      return labels[status] || status;
    },

    async acceptRequest(request) {
      this.processing = request.id;

      try {
        const result = await ChatService.acceptRequest(request.id);
        if (result.success) {
          // Remove from list or update status
          this.receivedRequests = this.receivedRequests.filter(
            (r) => r.id !== request.id
          );

          // Navigate to the new conversation if available
          if (result.data?.conversation_id) {
            this.$router.push({
              name: "ChatConversation",
              params: { conversationId: result.data.conversation_id },
            });
          }
        } else {
          this.error =
            this.$t("chat.failed_to_accept_request") ||
            "Failed to accept request";
        }
      } catch (e) {
        this.error =
          e?.message ||
          this.$t("chat.failed_to_accept_request") ||
          "Failed to accept request";
      } finally {
        this.processing = null;
      }
    },

    async rejectRequest(request) {
      this.processing = request.id;

      try {
        const result = await ChatService.rejectRequest(request.id);
        if (result.success) {
          this.receivedRequests = this.receivedRequests.filter(
            (r) => r.id !== request.id
          );
        } else {
          this.error =
            this.$t("chat.failed_to_reject_request") ||
            "Failed to reject request";
        }
      } catch (e) {
        this.error =
          e?.message ||
          this.$t("chat.failed_to_reject_request") ||
          "Failed to reject request";
      } finally {
        this.processing = null;
      }
    },

    async deleteRequest(request) {
      this.processing = request.id;

      try {
        const result = await ChatService.deleteRequest(request.id);
        if (result.success) {
          this.sentRequests = this.sentRequests.filter(
            (r) => r.id !== request.id
          );
        } else {
          this.error =
            this.$t("chat.failed_to_delete_request") ||
            "Failed to delete request";
        }
      } catch (e) {
        this.error =
          e?.message ||
          this.$t("chat.failed_to_delete_request") ||
          "Failed to delete request";
      } finally {
        this.processing = null;
      }
    },
  },
};
</script>

<style scoped>
.requests-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf4 100%);
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
}

.back-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #ffffff !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: background 0.2s;
}

.back-btn i {
  color: #ffffff !important;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.page-header h1 {
  flex: 1;
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
}

.header-spacer {
  width: 44px;
}

/* Tabs */
.tabs-container {
  display: flex;
  gap: 8px;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  background: #f1f5f9;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: #e2e8f0;
}

.tab-btn.active {
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
}

.tab-btn .badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 11px;
  font-size: 0.75rem;
  font-weight: 600;
}

.tab-btn.active .badge {
  background: rgba(255, 255, 255, 0.3);
}

/* Content */
.requests-content {
  padding: 20px 24px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 20px;
  color: #64748b;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 20px;
  color: #64748b;
  text-align: center;
}

.error-state i {
  font-size: 2.5rem;
  color: #ef4444;
}

.retry-btn {
  margin-top: 8px;
  padding: 10px 20px;
  background: #00c444;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 500;
}

.retry-btn:hover {
  background: #00a838;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #e8fff1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.empty-icon i {
  font-size: 2rem;
  color: #00c444;
}

.empty-state h3 {
  margin: 0 0 8px;
  color: #1a1a2e;
  font-size: 1.1rem;
}

.empty-state p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  max-width: 280px;
}

/* Requests List */
.requests-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.request-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
}

.request-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* User Avatar */
.user-avatar {
  flex-shrink: 0;
}

.user-avatar img {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
}

/* User Info */
.user-info {
  flex: 1;
  min-width: 0;
}

.user-info h4 {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
}

.request-time {
  font-size: 0.8rem;
  color: #94a3b8;
}

.request-message {
  margin-top: 10px;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 10px;
  border-left: 3px solid #00c444;
}

.request-message p {
  margin: 0;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.5;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-badge.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.accepted {
  background: #e8fff1;
  color: #00a838;
}

.status-badge.rejected {
  background: #fef2f2;
  color: #dc2626;
}

/* Request Actions */
.request-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.accept-btn {
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
}

.accept-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.3);
}

.reject-btn {
  background: #f1f5f9;
  color: #64748b;
}

.reject-btn:hover:not(:disabled) {
  background: #fee2e2;
  color: #dc2626;
}

.delete-btn {
  background: #fef2f2;
  color: #dc2626;
}

.delete-btn:hover:not(:disabled) {
  background: #fee2e2;
}

/* RTL Support */
[dir="rtl"] .page-header {
  flex-direction: row-reverse;
}

[dir="rtl"] .back-btn i {
  transform: scaleX(-1);
}

[dir="rtl"] .request-card {
  flex-direction: row-reverse;
}

[dir="rtl"] .request-message {
  border-left: none;
  border-right: 3px solid #00c444;
}

/* Responsive */
@media (max-width: 640px) {
  .request-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .request-actions {
    flex-direction: row;
    width: 100%;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
